import { useLayoutEffect, useReducer, useRef } from "preact/hooks";

type Listener = () => void;

export interface Store<T> {
  get(): T;
  set(patch: Partial<T> | ((s: T) => Partial<T>)): void;
  subscribe(l: Listener): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener>();
  return {
    get: () => state,
    set(patch) {
      const p = typeof patch === "function" ? patch(state) : patch;
      state = { ...state, ...p };
      listeners.forEach((l) => l());
    },
    subscribe(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

/** Subscribe a component to a slice of a store; re-renders only when the slice changes. */
export function useStore<T, S>(store: Store<T>, selector: (s: T) => S): S {
  const [, force] = useReducer((x: number) => x + 1, 0);
  const selRef = useRef(selector);
  selRef.current = selector;
  const value = selector(store.get());
  const valRef = useRef(value);
  valRef.current = value;
  useLayoutEffect(() => {
    const check = () => {
      if (!Object.is(selRef.current(store.get()), valRef.current)) force(0);
    };
    const unsubscribe = store.subscribe(check);
    // the store may have changed between this render and subscribing
    check();
    return unsubscribe;
  }, [store]);
  return value;
}

export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

// ------------------------------------------------------------------ saving

type Saved = Record<string, unknown>;

/**
 * Told about every save: the iOS app keeps a second copy, and a failed save gets a warning.
 * `replaced` hears when a record is replaced on purpose (Start over, Import, a restore).
 */
export const saveHooks: {
  saved?: (key: string, raw: string) => void;
  failed?: (key: string) => void;
  replaced?: (key: string) => void;
} = {};

/** Save a small value outside persist() (a flag, a count), telling the iOS app's copy too. */
export function saveRaw(key: string, raw: string): void {
  try {
    localStorage.setItem(key, raw);
    saveHooks.saved?.(key, raw);
  } catch {
    /* storage full or blocked: these only make the app ask or remind a little less precisely */
  }
}

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseSaved(raw: string | null): Saved | null {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * Merge two saves of the same record field by field: where `ours` changed a field since `base`,
 * ours wins; everywhere else `theirs` (the newer save) wins, including fields it removed.
 */
export function mergeSaved(base: Saved, ours: Saved, theirs: Saved): Saved {
  const out: Saved = {};
  for (const k of new Set([...Object.keys(base), ...Object.keys(ours), ...Object.keys(theirs)])) {
    const v = same(ours[k], base[k]) ? theirs[k] : ours[k];
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** The Name-off's results ({ elo, games, total, pairs }), merged name by name rather than as a whole. */
export function mergeRatings(base: Saved, ours: Saved, theirs: Saved): Saved {
  const sub = (k: string, s: Saved) => (s[k] && typeof s[k] === "object" ? (s[k] as Saved) : {});
  const n = (s: Saved) => (typeof s.total === "number" ? s.total : 0);
  return {
    elo: mergeSaved(sub("elo", base), sub("elo", ours), sub("elo", theirs)),
    games: mergeSaved(sub("games", base), sub("games", ours), sub("games", theirs)),
    pairs: mergeSaved(sub("pairs", base), sub("pairs", ours), sub("pairs", theirs)),
    // their duels, plus the ones this tab made since it last caught up
    total: n(theirs) + Math.max(0, n(ours) - n(base)),
  };
}

/**
 * Save a store in localStorage, and keep it in step with the app open in other tabs.
 *
 * A tab left open for days, or brought back from the browser's back-forward cache, still holds
 * what it last read. So whenever another tab has saved since, this one merges that in first (see
 * mergeSaved: for swipes, name by name) rather than saving over it. That way an old tab, or an old
 * version of the app still open after an update, can't wipe newer swipes or settings.
 */
export function persist<T extends object>(
  store: Store<T>,
  key: string,
  opts: {
    /** the part of the state that's saved (default: all of it) */
    toSaved?: (s: T) => Saved;
    /** the state change for a saved record (default: the record itself) */
    fromSaved?: (saved: Saved, s: T) => Partial<T>;
    /** batch quick changes into one save this many ms after the last */
    delay?: number;
    /** how this tab's changes and another tab's newer save combine (default: mergeSaved) */
    merge?: (base: Saved, ours: Saved, theirs: Saved) => Saved;
  } = {},
): void {
  const merge = opts.merge ?? mergeSaved;
  const toSaved = opts.toSaved ?? ((s: T) => s as Saved);
  const fromSaved = opts.fromSaved ?? ((saved: Saved) => saved as Partial<T>);
  /** the save this tab last read or wrote */
  let synced = readRaw(key);
  let timer = 0;
  let adopting = false;

  if (synced && parseSaved(synced) === null) {
    // unreadable: keep a copy before anything is saved over it
    try {
      if (localStorage.getItem(`${key}:unreadable`) === null) localStorage.setItem(`${key}:unreadable`, synced);
    } catch {
      /* no room for the copy */
    }
  }

  /** Take in a newer save from another tab, keeping this tab's unsaved changes on top. */
  function catchUp(): void {
    const raw = readRaw(key);
    if (raw === synced) return;
    if (raw === null) {
      // The save has gone, cleared from outside: the app never deletes it (Start over saves an
      // empty record). Browsers clear a site's storage on their own (Safari after a week without a
      // visit), even with a page open. This page still holds everything, so put it back rather than
      // taking the gap for an empty save.
      synced = null;
      write(JSON.stringify(toSaved(store.get())));
      return;
    }
    const theirs = parseSaved(raw);
    if (!theirs) return;
    const merged = merge(parseSaved(synced) ?? {}, toSaved(store.get()), theirs);
    synced = raw;
    adopting = true;
    try {
      store.set((s) => fromSaved(merged, s));
    } finally {
      adopting = false;
    }
  }

  function write(raw: string): void {
    try {
      localStorage.setItem(key, raw);
      synced = raw;
      saveHooks.saved?.(key, raw);
    } catch {
      // storage full or blocked: keep working in memory, and say so
      saveHooks.failed?.(key);
    }
  }

  function save(): void {
    clearTimeout(timer);
    timer = 0;
    catchUp();
    const raw = JSON.stringify(toSaved(store.get()));
    if (raw !== synced) write(raw);
  }

  store.subscribe(() => {
    if (adopting) return;
    if (!opts.delay) return save();
    clearTimeout(timer);
    timer = window.setTimeout(save, opts.delay);
  });
  window.addEventListener("storage", (e) => {
    if (e.key === key && e.storageArea === localStorage) catchUp();
  });
  // a hidden page may be closed or frozen without warning: save now; a page coming back may have
  // missed other tabs' saves while it was frozen
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (timer) save();
    } else catchUp();
  });
  window.addEventListener("pagehide", () => timer && save());
  window.addEventListener("pageshow", (e) => e.persisted && catchUp());
}
